import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@rudi/api/react';
import type { ClinicalTrialsPracticeSubmission } from './types';

export interface InvestigatorOption {
  slug: string;
  fullName: string;
  clinicNames: string[];
  studyCount: number;
}

export interface TeamClinicalSetup {
  teamId: string;
  principalInvestigatorSlug: string | null;
}

export interface TeamPracticeInput {
  name: string;
  address: string;
  postalCode: string;
  city: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
}

export function useTeamClinicalSetup(tenantId: string, teamId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  const path = `/cortex/clinical-trials/tenants/${tenantId}/teams/${teamId}`;
  const queryKey = ['team-clinical-setup', tenantId, teamId];
  const setup = useQuery({
    queryKey,
    queryFn: async () => (await api.get<TeamClinicalSetup>(path)).data,
    retry: false,
  });
  const investigators = useQuery({
    queryKey: ['cortex-investigator-options', tenantId, teamId],
    queryFn: async () =>
      (await api.get<InvestigatorOption[]>(`/access/teams/${teamId}/investigator-options`)).data,
    enabled: !!setup.data,
    staleTime: 60_000,
  });
  const practices = useQuery({
    queryKey: [...queryKey, 'practices'],
    queryFn: async () =>
      (await api.get<ClinicalTrialsPracticeSubmission[]>(`${path}/practices`)).data,
    enabled: !!setup.data,
  });
  const assign = useMutation({
    mutationFn: async (slug: string | null) =>
      (await api.put<TeamClinicalSetup>(path, { principalInvestigatorSlug: slug })).data,
    onSuccess: data => qc.setQueryData(queryKey, data),
  });
  const createPractice = useMutation({
    mutationFn: async (data: TeamPracticeInput) =>
      (await api.post<ClinicalTrialsPracticeSubmission>(`${path}/practices`, data)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey }),
  });
  return { setup, investigators, practices, assign, createPractice };
}
